import { getState } from '../state/store.js';
import { getSelectedSite } from '../sites/sites.js';
import { saveExpense, saveIncome, loadRecentTransactions } from '../transactions/transactions.js';
import { saveMaterial, loadMaterialsForSite } from '../materials/materials.js';
import { saveLabour, loadLabourForSite, computeLabourAmount } from '../labour/labour.js';
import { openFormSheet, closeFormSheet } from './modals.js';
import { todayISO, money } from './format.js';
import { loadProjectHealth, loadDashboardTransactions } from '../dashboard/projectHealth.js';

const EXPENSE_CATEGORIES = [
  'Materials', 'Labour', 'Transportation', 'Loading / Unloading',
  'Tools & Equipment', 'Site Electricity', 'Food / Tea', 'Accommodation',
  'Finishing', 'Other'
];
const PAYMENT_MODES = ['Cash', 'UPI', 'Bank', 'Credit', 'Other'];

function siteBadge() {
  const site = getSelectedSite();
  return `<div class="formSiteBadge">Site: <b>${site ? escapeHtml(site.name) : '—'}</b></div>`;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[m]));
}

export function openExpenseForm() {
  const state = getState();
  if (!state.selectedSiteId) {
    alert('Select a site first.');
    return;
  }
  const body = document.createElement('form');
  body.className = 'sheetForm';
  body.innerHTML = `
    ${siteBadge()}
    <label>Date<input name="entry_date" type="date" value="${todayISO()}" required></label>
    <label>Category<select name="category">${EXPENSE_CATEGORIES.map((c) => `<option>${c}</option>`).join('')}</select></label>
    <label>Description<input name="description" placeholder="What was this expense for?"></label>
    <label>Amount (₹)<input name="amount" type="number" min="0" step="0.01" required></label>
    <label>Payment Method<select name="payment_mode">${PAYMENT_MODES.map((p) => `<option>${p}</option>`).join('')}</select></label>
    <label>Notes<textarea name="notes" placeholder="Optional notes"></textarea></label>
    <div id="expenseFormError" class="formError" hidden></div>
    <button class="wideBtn" type="submit">Save Expense</button>
  `;

  body.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorBox = body.querySelector('#expenseFormError');
    errorBox.hidden = true;
    const submitBtn = body.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving…';

    const fd = new FormData(body);
    const result = await saveExpense({
      site_id: getState().selectedSiteId,
      entry_date: fd.get('entry_date'),
      category: fd.get('category'),
      description: fd.get('description'),
      amount: fd.get('amount'),
      payment_mode: fd.get('payment_mode'),
      notes: fd.get('notes')
    });

    if (!result.ok) {
      errorBox.textContent = result.error;
      errorBox.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Save Expense';
      return; // do NOT show success, do NOT close, until Supabase confirms
    }

    closeFormSheet();
    await Promise.all([
      loadRecentTransactions(getState().selectedSiteId),
      loadProjectHealth(),
      loadDashboardTransactions()
    ]);
  });

  openFormSheet('Add Expense', body);
}

export function openIncomeForm() {
  const state = getState();
  if (!state.selectedSiteId) {
    alert('Select a site first.');
    return;
  }
  const body = document.createElement('form');
  body.className = 'sheetForm';
  body.innerHTML = `
    ${siteBadge()}
    <label>Date<input name="entry_date" type="date" value="${todayISO()}" required></label>
    <label>Description<input name="description" placeholder="e.g. Client advance / running bill payment"></label>
    <label>Amount (₹)<input name="amount" type="number" min="0" step="0.01" required></label>
    <label>Payment Method<select name="payment_mode">${['Bank', 'UPI', 'Cash', 'Other'].map((p) => `<option>${p}</option>`).join('')}</select></label>
    <label>Notes<textarea name="notes" placeholder="Optional notes"></textarea></label>
    <div id="incomeFormError" class="formError" hidden></div>
    <button class="wideBtn incomeBtn" type="submit">Save Income</button>
  `;

  body.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorBox = body.querySelector('#incomeFormError');
    errorBox.hidden = true;
    const submitBtn = body.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving…';

    const fd = new FormData(body);
    const result = await saveIncome({
      site_id: getState().selectedSiteId,
      entry_date: fd.get('entry_date'),
      description: fd.get('description'),
      amount: fd.get('amount'),
      payment_mode: fd.get('payment_mode'),
      notes: fd.get('notes')
    });

    if (!result.ok) {
      errorBox.textContent = result.error;
      errorBox.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Save Income';
      return;
    }

    closeFormSheet();
    await Promise.all([
      loadRecentTransactions(getState().selectedSiteId),
      loadProjectHealth(),
      loadDashboardTransactions()
    ]);
  });

  openFormSheet('Add Income', body);
}

const UNITS = ['Bags', 'Nos', 'Pack', 'Kg', 'Litre', 'Sqft', 'Other'];

export function openMaterialForm() {
  const state = getState();
  if (!state.selectedSiteId) {
    alert('Select a site first.');
    return;
  }
  const body = document.createElement('form');
  body.className = 'sheetForm';
  body.innerHTML = `
    ${siteBadge()}
    <label>Date<input name="purchase_date" type="date" value="${todayISO()}" required></label>
    <label>Material<input name="material_name" placeholder="e.g. Gypsum, HD MR Bag, Wire" required></label>
    <label>Quantity<input name="quantity" type="number" min="0" step="0.01" required></label>
    <label>Unit<select name="unit">${UNITS.map((u) => `<option>${u}</option>`).join('')}</select></label>
    <label>Rate (₹)<input name="rate" type="number" min="0" step="0.01" required></label>
    <label>Amount (₹)<input name="amount_display" readonly value="₹0"></label>
    <label>Supplier<input name="supplier" placeholder="Optional"></label>
    <label>Bill / Invoice<input name="bill_file" type="file" accept=".jpg,.jpeg,.png,.pdf"></label>
    <label>Payment Method<select name="payment_mode">${PAYMENT_MODES.map((p) => `<option>${p}</option>`).join('')}</select></label>
    <label>Notes<textarea name="notes" placeholder="Optional notes"></textarea></label>
    <div id="materialFormError" class="formError" hidden></div>
    <button class="wideBtn" type="submit">Save Purchase</button>
  `;

  const qtyEl = body.querySelector('[name="quantity"]');
  const rateEl = body.querySelector('[name="rate"]');
  const amountEl = body.querySelector('[name="amount_display"]');
  const recalc = () => {
    amountEl.value = money((Number(qtyEl.value) || 0) * (Number(rateEl.value) || 0));
  };
  qtyEl.addEventListener('input', recalc);
  rateEl.addEventListener('input', recalc);

  body.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorBox = body.querySelector('#materialFormError');
    errorBox.hidden = true;
    const submitBtn = body.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving…';

    const fd = new FormData(body);
    const billFile = fd.get('bill_file');
    const result = await saveMaterial({
      site_id: getState().selectedSiteId,
      purchase_date: fd.get('purchase_date'),
      material_name: fd.get('material_name'),
      unit: fd.get('unit'),
      quantity: fd.get('quantity'),
      rate: fd.get('rate'),
      supplier: fd.get('supplier'),
      payment_mode: fd.get('payment_mode'),
      notes: fd.get('notes')
    }, billFile && billFile.size > 0 ? billFile : null);

    if (!result.ok) {
      errorBox.textContent = result.error;
      errorBox.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Save Purchase';
      return; // do NOT show success, do NOT close, until the RPC confirms
    }

    closeFormSheet();
    const siteId = getState().selectedSiteId;
    await Promise.all([
      loadRecentTransactions(siteId),
      loadMaterialsForSite(siteId),
      loadProjectHealth(),
      loadDashboardTransactions()
    ]);
  });

  openFormSheet('Add Material Purchase', body);
}

function labourBasisFieldsHtml(basis) {
  if (basis === 'daily') {
    return `
      <label>Days<input name="days" type="number" min="0" step="0.01" required></label>
      <label>Daily Rate (₹)<input name="daily_rate" type="number" min="0" step="0.01" required></label>
    `;
  }
  if (basis === 'sqft') {
    return `
      <label>Sqft<input name="sqft" type="number" min="0" step="0.01" required></label>
      <label>Rate / Sqft (₹)<input name="sqft_rate" type="number" min="0" step="0.01" required></label>
    `;
  }
  return `<label>Contract Amount (₹)<input name="contract_amount" type="number" min="0" step="0.01" required></label>`;
}

export function openLabourForm() {
  const state = getState();
  if (!state.selectedSiteId) {
    alert('Select a site first.');
    return;
  }
  const body = document.createElement('form');
  body.className = 'sheetForm';
  body.innerHTML = `
    ${siteBadge()}
    <label>Date<input name="payment_date" type="date" value="${todayISO()}" required></label>
    <label>Worker<input name="worker_name" placeholder="e.g. Afsal (Mason)" required></label>
    <label>Payment Basis<select name="basis">
      <option value="daily">Daily</option>
      <option value="sqft">Sqft</option>
      <option value="contract">Contract</option>
    </select></label>
    <div id="labourBasisFields">${labourBasisFieldsHtml('daily')}</div>
    <label>Amount (₹)<input name="amount_display" readonly value="₹0"></label>
    <label>Payment Method<select name="payment_mode">${PAYMENT_MODES.map((p) => `<option>${p}</option>`).join('')}</select></label>
    <label>Notes<textarea name="notes" placeholder="Optional notes"></textarea></label>
    <div id="labourFormError" class="formError" hidden></div>
    <button class="wideBtn" type="submit">Save Labour Payment</button>
  `;

  const basisSelect = body.querySelector('[name="basis"]');
  const basisFieldsWrap = body.querySelector('#labourBasisFields');
  const amountEl = body.querySelector('[name="amount_display"]');

  function currentBasisValues() {
    return {
      days: body.querySelector('[name="days"]')?.value,
      daily_rate: body.querySelector('[name="daily_rate"]')?.value,
      sqft: body.querySelector('[name="sqft"]')?.value,
      sqft_rate: body.querySelector('[name="sqft_rate"]')?.value,
      contract_amount: body.querySelector('[name="contract_amount"]')?.value
    };
  }
  function recalc() {
    amountEl.value = money(computeLabourAmount(basisSelect.value, currentBasisValues()));
  }
  function wireBasisInputs() {
    basisFieldsWrap.querySelectorAll('input').forEach((el) => el.addEventListener('input', recalc));
  }
  wireBasisInputs();

  basisSelect.addEventListener('change', () => {
    basisFieldsWrap.innerHTML = labourBasisFieldsHtml(basisSelect.value);
    wireBasisInputs();
    recalc();
  });

  body.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorBox = body.querySelector('#labourFormError');
    errorBox.hidden = true;
    const submitBtn = body.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving…';

    const fd = new FormData(body);
    const result = await saveLabour({
      site_id: getState().selectedSiteId,
      payment_date: fd.get('payment_date'),
      worker_name: fd.get('worker_name'),
      basis: fd.get('basis'),
      days: fd.get('days'),
      daily_rate: fd.get('daily_rate'),
      sqft: fd.get('sqft'),
      sqft_rate: fd.get('sqft_rate'),
      contract_amount: fd.get('contract_amount'),
      payment_mode: fd.get('payment_mode'),
      notes: fd.get('notes')
    });

    if (!result.ok) {
      errorBox.textContent = result.error;
      errorBox.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Save Labour Payment';
      return; // do NOT show success, do NOT close, until the RPC confirms
    }

    closeFormSheet();
    const siteId = getState().selectedSiteId;
    await Promise.all([
      loadRecentTransactions(siteId),
      loadLabourForSite(siteId),
      loadProjectHealth(),
      loadDashboardTransactions()
    ]);
  });

  openFormSheet('Add Labour Payment', body);
}
